import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Desk } from '@/components/shell/Desk.tsx'
import { Spinner } from '@/components/ui/primitives.tsx'
import { useSession } from '@/hooks/use-api.ts'
import { useT } from '@/i18n/index.ts'

/**
 * Auth gate.
 *
 * Renders the wait, never the login screen, while the session resolves — a signed-in user
 * reloading the page should never see a sign-in form appear and vanish. The attempted path is
 * carried through so they land back where they were.
 *
 * The wait is painted on the same desk the shell is about to paint, so the room is already
 * there when the app arrives rather than fading in behind it a moment later.
 */
export const RequireAuth = ({ children }: { children: ReactNode }) => {
  const t = useT()
  const session = useSession()
  const location = useLocation()

  if (session.isLoading) {
    return (
      <div className="relative flex h-dvh items-center justify-center bg-desk text-fg">
        <Desk />
        {/* The spinner is decoration and `aria-hidden`; this sentence is the whole
            announcement, and `relative` keeps it and the spinner above the aurora. */}
        <span className="sr-only">{t.shell.checkingSession}</span>
        <Spinner size={18} className="relative text-fg-subtle" />
      </div>
    )
  }

  if (!session.data?.authenticated) {
    const redirectTo = `${location.pathname}${location.search}`
    return <Navigate to={`/login?redirectTo=${encodeURIComponent(redirectTo)}`} replace />
  }

  return <>{children}</>
}
